import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import VisitModal from './VisitModal';
import styles from './AdminAlerts.module.css';

const TIPO_META = {
  luz:  { label: 'Electricidad', emoji: '⚡', color: '#F59E0B' },
  agua: { label: 'Agua',         emoji: '💧', color: '#3B82F6' },
  gas:  { label: 'Gas',          emoji: '🔥', color: '#EF4444' },
};

const SEVERIDAD = { sin_deteccion: 0, no_es_medidor: 1, discrepancia: 2, sin_acceso: 3, baja_confianza: 4 };

function tipoAlerta(a) {
  if (a.sin_acceso)       return 'sin_acceso';
  if (a.es_medidor === 0) return 'no_es_medidor';
  if (!a.lectura_ocr)     return 'sin_deteccion';
  if (a.lectura_ocr && a.lectura_confirmada && a.lectura_ocr !== a.lectura_confirmada)
                          return 'discrepancia';
  return 'baja_confianza';
}

const BADGE_META = {
  sin_deteccion:  { label: 'Sin detección',  cls: 'badgeRojo',     icon: '📷' },
  no_es_medidor:  { label: 'No es medidor',  cls: 'badgeRojo',     icon: '🚫' },
  discrepancia:   { label: 'Discrepancia',   cls: 'badgeAmbar',    icon: '⚠️' },
  sin_acceso:     { label: 'Sin acceso',     cls: 'badgeMorado',   icon: '🔒' },
  baja_confianza: { label: 'Baja confianza', cls: 'badgeAmarillo', icon: '🔍' },
};

// Color del borde izquierdo de la tarjeta de visita según la alerta más grave
function severidadVisita(alertas) {
  const tipos = alertas.map(tipoAlerta);
  const min = Math.min(...tipos.map(t => SEVERIDAD[t]));
  if (min <= 1) return 'visita-roja';
  if (min === 2) return 'visita-ambar';
  if (min === 3) return 'visita-morada';
  return 'visita-amarilla';
}

export default function AdminAlerts() {
  const [alerts, setAlerts]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [viewVisitId, setViewVisitId] = useState(null);
  const [lightbox, setLightbox]       = useState(null);
  const [editing, setEditing]         = useState(null);
  const [newVal, setNewVal]           = useState('');
  const [saving, setSaving]           = useState(false);
  const [openVisits, setOpenVisits]   = useState(new Set());
  const [exitingIds, setExitingIds]   = useState(new Set()); // medidor_ids saliendo con animación
  const [notification, setNotification] = useState(null);   // { mensaje, tipo: 'aprobada'|'rechazada' }
  const [search, setSearch]           = useState('');

  // true solo durante la carga inicial; las recargas post-acción no muestran spinner
  // ni resetean los acordeones abiertos
  const isFirstLoad = useRef(true);

  const load = () => {
    if (isFirstLoad.current) setLoading(true);
    api.get('/admin/alerts')
      .then(r => {
        setAlerts(r.data);
        if (isFirstLoad.current) {
          setOpenVisits(new Set()); // acordeones cerrados solo al entrar por primera vez
          isFirstLoad.current = false;
        }
        // En recargas posteriores: openVisits se preserva tal cual
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Lógica unificada: anima la salida del item y lo elimina del estado local
  // sin recargar la página ni hacer scroll
  const doResolve = async (id, payload) => {
    setSaving(true);
    try {
      const response = await api.patch(`/admin/medidores/${id}`, payload);

      // Notificación si la visita se cerró automáticamente
      if (response.data?.visita_auto) {
        const va = response.data.visita_auto;
        const mensaje = va.estado === 'aprobada'
          ? `✅ Visita #${va.id} aprobada automáticamente`
          : `❌ Visita #${va.id} rechazada — ${va.motivo_rechazo}`;
        setNotification({ mensaje, tipo: va.estado });
        setTimeout(() => setNotification(null), 6000);
      }

      // 1. Activar animación de salida (fade + colapso)
      setExitingIds(prev => new Set([...prev, id]));
      // 2. Eliminar del estado local cuando la animación termina (0.65 s)
      setTimeout(() => {
        setAlerts(prev => prev.filter(a => a.medidor_id !== id));
        setExitingIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      }, 650);
    } finally { setSaving(false); }
  };

  const resolver = (id, estado_revision_ocr, lectura_confirmada = null) =>
    doResolve(id, { estado_revision_ocr, lectura_confirmada });

  const confirmarLectura = async (id) => {
    if (!newVal.trim()) return;
    setEditing(null);
    setNewVal('');
    await doResolve(id, { estado_revision_ocr: 'corregido', lectura_confirmada: newVal.trim() });
  };

  const toggleVisit = (id) => {
    setOpenVisits(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Agrupar alertas por visita
  const grupos = useMemo(() => {
    const map = {};
    alerts.forEach(a => {
      if (!map[a.visita_id]) {
        map[a.visita_id] = {
          visita_id: a.visita_id, ciudad: a.ciudad, conjunto: a.conjunto,
          torre: a.torre, apartamento: a.apartamento, auditor: a.auditor,
          fecha: a.fecha, alertas: [],
        };
      }
      map[a.visita_id].alertas.push(a);
    });
    return Object.values(map).sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
  }, [alerts]);

  // Filtrar por búsqueda
  const gruposFiltrados = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return grupos;
    return grupos.filter(g =>
      String(g.visita_id).includes(q) ||
      g.apartamento?.toLowerCase().includes(q) ||
      g.torre?.toLowerCase().includes(q) ||
      g.conjunto?.toLowerCase().includes(q) ||
      g.ciudad?.toLowerCase().includes(q) ||
      g.auditor?.toLowerCase().includes(q)
    );
  }, [grupos, search]);

  if (loading) return (
    <div className={styles.loadingWrap}>
      <div className={styles.spinner} />
      <span>Cargando alertas OCR...</span>
    </div>
  );

  if (!alerts.length) return (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon}>✅</div>
      <h3 className={styles.emptyTitle}>Sin alertas pendientes</h3>
      <p className={styles.emptyDesc}>Todos los medidores han sido revisados.</p>
    </div>
  );

  return (
    <div className={styles.page}>

      {/* Banner de auto-aprobación / rechazo */}
      {notification && (
        <div className={`${styles.notification} ${styles[`notification${notification.tipo === 'aprobada' ? 'Ok' : 'Err'}`]}`}>
          <span>{notification.mensaje}</span>
          <button className={styles.notifClose} onClick={() => setNotification(null)}>✕</button>
        </div>
      )}

      {/* Encabezado */}
      <div className={styles.pageHeader}>
        <div>
          <h2 className={styles.pageTitle}>Alertas OCR</h2>
          <p className={styles.pageSubtitle}>Revisión de medidores con hallazgos pendientes</p>
        </div>
        <div className={styles.pageStats}>
          <div className={styles.statChip}>
            <span className={styles.statNum}>{grupos.length}</span>
            <span className={styles.statLabel}>visitas</span>
          </div>
          <div className={`${styles.statChip} ${styles.statChipAlert}`}>
            <span className={styles.statNum}>{alerts.length}</span>
            <span className={styles.statLabel}>medidores</span>
          </div>
        </div>
      </div>

      {/* Buscador */}
      <div className={styles.searchBar}>
        <span className={styles.searchIcon}>🔍</span>
        <input
          type="search"
          className={styles.searchInput}
          placeholder="Buscar por apto, conjunto, auditor, ciudad o #visita…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {search && (
          <button className={styles.searchClear} onClick={() => setSearch('')} title="Limpiar">✕</button>
        )}
      </div>

      {/* Conteo cuando hay filtro activo */}
      {search && (
        <p className={styles.searchCount}>
          {gruposFiltrados.length === 0
            ? `Sin resultados para "${search}"`
            : `${gruposFiltrados.length} visita${gruposFiltrados.length !== 1 ? 's' : ''} encontrada${gruposFiltrados.length !== 1 ? 's' : ''}`}
        </p>
      )}

      {/* Lista acordeón por visita */}
      <div className={styles.list}>
        {gruposFiltrados.map(grupo => {
          const isOpen  = openVisits.has(grupo.visita_id);
          const sevCls  = severidadVisita(grupo.alertas);

          return (
            <div key={grupo.visita_id} className={`${styles.visitCard} ${styles[sevCls]}`}>

              {/* ── Header acordeón ── */}
              <button
                className={`${styles.visitHeader} ${isOpen ? styles.visitHeaderOpen : ''}`}
                onClick={() => toggleVisit(grupo.visita_id)}
              >
                <div className={styles.visitHeaderLeft}>
                  <div className={styles.visitTitulo}>
                    <span className={styles.visitId}>Visita #{grupo.visita_id}</span>
                    <span className={styles.visitApto}>
                      Apto {grupo.apartamento}{grupo.torre ? ` · Torre ${grupo.torre}` : ''}
                    </span>
                  </div>
                  <div className={styles.visitMeta}>
                    <span className={styles.metaChip}>🏘️ {grupo.conjunto} · {grupo.ciudad}</span>
                    <span className={styles.metaChip}>👤 {grupo.auditor}</span>
                    <span className={styles.metaChip}>
                      📅 {new Date(grupo.fecha).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                </div>
                <div className={styles.visitHeaderRight}>
                  <span className={styles.alertCountBadge}>
                    {grupo.alertas.length} alerta{grupo.alertas.length !== 1 ? 's' : ''}
                  </span>
                  <span className={`${styles.toggleBtn} ${isOpen ? styles.toggleBtnOpen : ''}`}>
                    {isOpen ? 'Ocultar' : 'Ver hallazgos'}
                    <span className={`${styles.chevron} ${isOpen ? styles.chevronOpen : ''}`}>▼</span>
                  </span>
                </div>
              </button>

              {/* ── Cuerpo acordeón ── */}
              {isOpen && (
                <div className={styles.visitBody}>
                  <button
                    className={styles.btnVerVisita}
                    onClick={() => setViewVisitId(grupo.visita_id)}
                  >
                    <span className={styles.btnVerIcon}>🔍</span>
                    Ver visita completa
                  </button>

                  {/* Tarjetas de medidor */}
                  <div className={styles.medList}>
                    {grupo.alertas.map(a => {
                      const meta       = TIPO_META[a.tipo];
                      const tipo       = tipoAlerta(a);
                      const badge      = BADGE_META[tipo];
                      const sinDet     = tipo === 'sin_deteccion';
                      const sinAcceso  = tipo === 'sin_acceso';
                      const noEsMed    = tipo === 'no_es_medidor';
                      const discrep    = tipo === 'discrepancia';
                      const isEditing  = editing === a.medidor_id;
                      const isExiting  = exitingIds.has(a.medidor_id);
                      // Diferencia numérica > 15%: bloquear aprobación rápida
                      const diffPct    = a.ocr_diff_pct ? parseFloat(a.ocr_diff_pct) : null;
                      const diffAlta   = discrep && diffPct !== null && diffPct > 15;

                      return (
                        <div
                          key={a.medidor_id}
                          className={`${styles.medCardWrapper} ${isExiting ? styles.medCardWrapperExiting : ''}`}
                        >
                        <div className={`${styles.medCard} ${styles[`med-${tipo}`]}`}>

                          {/* Cabecera medidor */}
                          <div className={styles.medHeader}>
                            <div className={styles.medTipo}>
                              <span className={styles.medEmoji} style={{ color: meta.color }}>{meta.emoji}</span>
                              <span className={styles.medLabel}>Medidor de {meta.label}</span>
                            </div>
                            <span className={`${styles.badge} ${styles[badge.cls]}`}>
                              {badge.icon} {badge.label}
                            </span>
                          </div>

                          {/* Foto */}
                          {a.foto_path && (
                            <div className={styles.fotoWrap}>
                              <img
                                src={`/uploads/${a.foto_path}`}
                                alt="Foto medidor"
                                className={styles.foto}
                                onClick={() => setLightbox(`/uploads/${a.foto_path}`)}
                              />
                              <span className={styles.fotoHint}>Toca para ampliar</span>
                            </div>
                          )}

                          {/* ── Sin acceso ── */}
                          {sinAcceso && (
                            <div className={`${styles.alertBox} ${styles.alertBoxMorado}`}>
                              <p className={styles.alertBoxTitle}>El auditor no pudo acceder a este medidor</p>
                              {a.motivo_sin_acceso && (
                                <p className={styles.alertBoxNota}>"{a.motivo_sin_acceso}"</p>
                              )}
                              <p className={styles.alertBoxDesc}>
                                Aprueba si el motivo es válido, o rechaza para que el auditor vuelva a intentarlo.
                              </p>
                            </div>
                          )}

                          {/* ── No es medidor ── */}
                          {noEsMed && (
                            <div className={`${styles.alertBox} ${styles.alertBoxRojo}`}>
                              <p className={styles.alertBoxTitle}>La IA detectó que esta foto no corresponde a un medidor</p>
                              {a.nota_ocr && (
                                <p className={styles.alertBoxNota}>"{a.nota_ocr}"</p>
                              )}
                              <p className={styles.alertBoxDesc}>
                                Aprueba si el contexto lo justifica, o rechaza para que se vuelva a registrar.
                              </p>
                            </div>
                          )}

                          {/* ── Sin detección ── */}
                          {sinDet && (
                            <div className={`${styles.alertBox} ${styles.alertBoxRojo}`}>
                              <p className={styles.alertBoxTitle}>La IA no pudo leer el número del medidor</p>
                              {a.nota_ocr && (
                                <p className={styles.alertBoxNota}>"{a.nota_ocr}"</p>
                              )}
                              {a.lectura_confirmada ? (
                                <>
                                  <div className={styles.auditorLecturaBox}>
                                    <span className={styles.auditorLecturaLabel}>📋 Lectura registrada por el auditor:</span>
                                    <code className={styles.auditorLecturaVal}>{a.lectura_confirmada}</code>
                                  </div>
                                  <p className={styles.alertBoxDesc}>
                                    Confirma la lectura del auditor revisando la foto, corrígela si es necesario, o rechaza para que retome la foto.
                                  </p>
                                </>
                              ) : (
                                <p className={styles.alertBoxDesc}>
                                  Ingresa la lectura correcta revisando la foto, o rechaza para que el auditor retome la foto.
                                </p>
                              )}
                            </div>
                          )}

                          {/* ── Lecturas: discrepancia / baja confianza ── */}
                          {!sinDet && !sinAcceso && !noEsMed && (
                            <div className={styles.lecturas}>
                              {discrep && (
                                diffAlta ? (
                                  <div className={`${styles.alertBox} ${styles.alertBoxRojo}`}>
                                    <p className={styles.alertBoxTitle}>
                                      🚨 Diferencia del <strong>{diffPct.toFixed(1)}%</strong> — Corrección muy alta
                                    </p>
                                    <p className={styles.alertBoxDesc}>
                                      La diferencia entre lo que detectó la IA y lo que registró el auditor supera el 15%.
                                      <br/>
                                      <strong>Revisa la foto con cuidado</strong> y verifica que el medidor fotografiado
                                      corresponda al apartamento. Debes ingresar la lectura correcta manualmente.
                                    </p>
                                  </div>
                                ) : (
                                  <div className={`${styles.alertBox} ${styles.alertBoxAmbar}`}>
                                    <p className={styles.alertBoxTitle}>
                                      La IA y el auditor registraron valores diferentes
                                    </p>
                                    <p className={styles.alertBoxDesc}>Verifica la foto y confirma el valor correcto.</p>
                                  </div>
                                )
                              )}
                              <div className={styles.lecturaGrid}>
                                <div className={styles.lecturaItem}>
                                  <span className={styles.lecturaLabel}>Lectura IA</span>
                                  <code className={styles.lecturaVal}>{a.lectura_ocr ?? '—'}</code>
                                </div>
                                <div className={discrep ? styles.lecturaItemDest : styles.lecturaItem}>
                                  <span className={styles.lecturaLabel}>Lectura Auditor</span>
                                  <code className={`${styles.lecturaVal} ${discrep ? styles.lecturaValDest : ''}`}>
                                    {a.lectura_confirmada ?? '—'}
                                  </code>
                                </div>
                              </div>
                              {a.nota_ocr && (
                                <div className={styles.notaOcr}>
                                  <span className={styles.notaOcrIcon}>💬</span>
                                  <span>{a.nota_ocr}</span>
                                </div>
                              )}
                              {a.calidad_foto && a.calidad_foto !== 'buena' && (
                                <div className={`${styles.notaOcr} ${a.calidad_foto === 'mala' ? styles.notaOcrMala : ''}`}>
                                  <span className={styles.notaOcrIcon}>📷</span>
                                  <span>Calidad: <strong>{a.calidad_foto}</strong>{a.motivo_calidad && ` — ${a.motivo_calidad}`}</span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* ── Formulario corrección ── */}
                          {isEditing && (
                            <div className={styles.editForm}>
                              <label className={styles.editLabel}>Ingresa la lectura correcta:</label>
                              <div className={styles.editRow}>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={newVal}
                                  onChange={e => setNewVal(e.target.value)}
                                  placeholder="Ej: 00201.126"
                                  className={styles.editInput}
                                  autoFocus
                                />
                                <button
                                  className={styles.btnGuardar}
                                  onClick={() => confirmarLectura(a.medidor_id)}
                                  disabled={!newVal.trim() || saving}
                                >
                                  {saving ? '...' : '✓ Guardar'}
                                </button>
                                <button
                                  className={styles.btnCancelar}
                                  onClick={() => { setEditing(null); setNewVal(''); }}
                                >
                                  Cancelar
                                </button>
                              </div>
                            </div>
                          )}

                          {/* ── Botones de acción ── */}
                          {!isEditing && (
                            <div className={styles.actions}>
                              {/* Sin acceso / No es medidor */}
                              {(sinAcceso || noEsMed) && (
                                <>
                                  <button
                                    className={styles.btnConfirmar}
                                    onClick={() => resolver(a.medidor_id, 'aprobado')}
                                    disabled={saving}
                                  >
                                    ✓ Aprobar
                                  </button>
                                  <button
                                    className={styles.btnRechazar}
                                    onClick={() => resolver(a.medidor_id, 'rechazado')}
                                    disabled={saving}
                                  >
                                    ✕ Rechazar
                                  </button>
                                </>
                              )}

                              {/* Sin detección */}
                              {sinDet && (
                                <>
                                  {a.lectura_confirmada && (
                                    <button
                                      className={styles.btnConfirmar}
                                      onClick={() => resolver(a.medidor_id, 'aprobado', a.lectura_confirmada)}
                                      disabled={saving}
                                    >
                                      ✓ Confirmar lectura auditor
                                    </button>
                                  )}
                                  <button
                                    className={styles.btnCorregir}
                                    onClick={() => { setEditing(a.medidor_id); setNewVal(a.lectura_confirmada || ''); }}
                                  >
                                    📝 {a.lectura_confirmada ? 'Corregir' : 'Ingresar lectura'}
                                  </button>
                                  <button
                                    className={styles.btnRechazar}
                                    onClick={() => resolver(a.medidor_id, 'rechazado')}
                                    disabled={saving}
                                  >
                                    ✕ Rechazar
                                  </button>
                                </>
                              )}

                              {/* Discrepancia */}
                              {discrep && (
                                <>
                                  {/* Diferencia baja (≤15%): se puede confirmar con un clic */}
                                  {!diffAlta && (
                                    <button
                                      className={styles.btnConfirmar}
                                      onClick={() => resolver(a.medidor_id, 'aprobado', a.lectura_confirmada)}
                                      disabled={saving}
                                    >
                                      ✓ Confirmar auditor
                                    </button>
                                  )}
                                  {/* Diferencia alta (>15%): OBLIGA a verificar manualmente */}
                                  <button
                                    className={diffAlta ? styles.btnCorregirAlta : styles.btnCorregir}
                                    onClick={() => { setEditing(a.medidor_id); setNewVal(a.lectura_confirmada || a.lectura_ocr || ''); }}
                                  >
                                    {diffAlta ? '🔍 Verificar y confirmar lectura' : '✏️ Corregir'}
                                  </button>
                                  <button
                                    className={styles.btnRechazar}
                                    onClick={() => resolver(a.medidor_id, 'rechazado')}
                                    disabled={saving}
                                  >
                                    ✕ Rechazar
                                  </button>
                                </>
                              )}

                              {/* Baja confianza */}
                              {!sinDet && !sinAcceso && !noEsMed && !discrep && (
                                <>
                                  <button
                                    className={styles.btnConfirmar}
                                    onClick={() => resolver(a.medidor_id, 'aprobado', a.lectura_confirmada || a.lectura_ocr)}
                                    disabled={saving}
                                  >
                                    ✓ Confirmar lectura
                                  </button>
                                  <button
                                    className={styles.btnCorregir}
                                    onClick={() => { setEditing(a.medidor_id); setNewVal(a.lectura_confirmada || a.lectura_ocr || ''); }}
                                  >
                                    ✏️ Corregir
                                  </button>
                                  <button
                                    className={styles.btnRechazar}
                                    onClick={() => resolver(a.medidor_id, 'rechazado')}
                                    disabled={saving}
                                  >
                                    ✕ Rechazar
                                  </button>
                                </>
                              )}
                            </div>
                          )}

                        </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {viewVisitId && (
        <VisitModal
          visitId={viewVisitId}
          onClose={() => setViewVisitId(null)}
          onUpdated={() => { setViewVisitId(null); load(); }}
        />
      )}

      {lightbox && (
        <div className={styles.lightboxOverlay} onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="Foto ampliada" className={styles.lightboxImg} />
          <button className={styles.lightboxClose} onClick={() => setLightbox(null)}>✕</button>
        </div>
      )}
    </div>
  );
}
